<template>
  <div class="mypage-contract-log-pane">
    <div class="mypage-contract-log-pane__header">
      <p
        class="mypage-contract-log-pane__lead"
        v-html="$tc('MypagePage.MypageContract.logPage.lead')"
      />
      <p
        class="mypage-contract-log-pane__message"
        v-if="appMessageAndExternalLinks.message"
        v-html="appMessageAndExternalLinks.message"
      />
      <ul class="mypage-contract-log-pane__list" v-if="appMessageAndExternalLinks.links.length > 0">
        <li
          v-for="(link, index) in appMessageAndExternalLinks.links"
          :key="index"
          class="mypage-contract-log-pane__item"
        >
          <atom-link
            :url="link.url"
            :linkText="link.text"
            :imgPath="require('@/assets/img/icon/icon_external_link_black.svg')"
            class="mypage-contract-log-pane__itemLink"
          />
        </li>
      </ul>
      <template v-if="appAdditionalMessage && appAdditionalExternalLinks.length > 0">
        <p class="mypage-contract-log-pane__message" v-html="appAdditionalMessage" />
        <ul class="mypage-contract-log-pane__list">
          <li
            v-for="(link, index) in appAdditionalExternalLinks"
            :key="index"
            class="mypage-contract-log-pane__item"
          >
            <atom-link
              :url="link.url"
              :linkText="link.text"
              :imgPath="require('@/assets/img/icon/icon_external_link_black.svg')"
              class="mypage-contract-log-pane__itemLink"
            />
          </li>
        </ul>
      </template>
    </div>
    <mypage-contract-log-table-section
      v-if="planContractLogData"
      :planContractLogData="planContractLogData"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, ref, Ref } from '@vue/composition-api'
import VueRouter from 'vue-router'
import MypageContractLogTableSection from '@/components/MypageContractPage/MypageContractLogPane/MypageContractLogTableSection.vue'
import useMypageContractLog, {
  PlanContractLogDataType,
} from '@/components/MypageContractPage/hook/useMypageContractLog'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import I18n from '@/locales/I18n'
import AtomLink from '@/components/atoms/AtomLink.vue'
import DeviceInfo from '@/util/DeviceInfo'
import Const from '@/util/Const'
import StoreUtil from '@/store/StoreUtil'

/**
 * アプリまたはアプリ内課金している場合に表示するメッセージと表示する外部リンク集の型
 */
type AppMessageAndExternalLinkType = {
  message: string
  links: Array<{
    text: string
    url: string
  }>
}

/**
 * マイページ: プラン契約履歴 コンテンツペイン
 */
export default defineComponent({
  name: 'MypageContractLogPane',
  components: {
    AtomLink,
    MypageContractLogTableSection,
  },
  setup() {
    const contractInfoStore = StoreUtil.useStore('ContractInfoStore')
    const { paymentMethod } = contractInfoStore
    const { fetchPlanContractLogData } = useMypageContractLog()

    const router = inject('router') as VueRouter

    /** プラン契約履歴に表示するデータ */
    const planContractLogData: Ref<Array<PlanContractLogDataType> | null> = ref(null)

    /** プラン契約履歴に表示するデータを取得 */
    const fetchPlanContractLog = async () => {
      const result = await fetchPlanContractLogData()
      if (!result) {
        await MessageDialogStore.value.open({
          title: I18n.tc('MypagePage.MypageContract.logPage.errors.fetchPlanContractLogData.title'),
          message: I18n.tc(
            'MypagePage.MypageContract.logPage.errors.fetchPlanContractLogData.message',
          ),
        })
        await router.replace({ name: 'MypageTopPage' })
        return
      }
      // 取得結果をセット
      planContractLogData.value = result
    }
    fetchPlanContractLog()

    // アプリまたはアプリ内課金している場合に表示するメッセージと表示する外部リンク集
    const appMessageAndExternalLinks = computed((): AppMessageAndExternalLinkType => {
      if (DeviceInfo.isiOS() || (!DeviceInfo.isCordova() && paymentMethod === 'AppStore')) {
        return {
          message: I18n.tc('MypagePage.MypageContract.logPage.message.iOS'),
          links: [
            {
              text: I18n.tc('MypagePage.MypageContract.logPage.history.iOS'),
              url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.HISTORY.IOS,
            },
            {
              text: I18n.tc('MypagePage.MypageContract.logPage.history.iOSSubscription'),
              url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.SUBSCRIPTION_MANAGEMENT.IOS,
            },
          ],
        }
      }
      if (DeviceInfo.isAndroid() || (!DeviceInfo.isCordova() && paymentMethod === 'GooglePlay')) {
        return {
          message: I18n.tc('MypagePage.MypageContract.logPage.message.android'),
          links: [
            {
              text: I18n.tc('MypagePage.MypageContract.logPage.history.android'),
              url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.HISTORY.ANDROID,
            },
          ],
        }
      }

      return {
        message: '',
        links: [],
      }
    })

    /**
     * アプリの場合に表示するメッセージ。以下の場合に表示する。
     * 1. Google Playで課金しiOS機器でプラン契約履歴を開く
     * 2. App Storeで課金しAndroidでプラン契約履歴を開く
     */
    const appAdditionalMessage = computed(() => {
      if (DeviceInfo.isiOS() && paymentMethod === 'GooglePlay') {
        return I18n.tc('MypagePage.MypageContract.logPage.message.android')
      }
      if (DeviceInfo.isAndroid() && paymentMethod === 'AppStore') {
        return I18n.tc('MypagePage.MypageContract.logPage.message.iOSWebOnly')
      }
      return ''
    })

    /**
     * スマートフォン用の購入履歴確認リンク集。以下の場合に表示する。
     * 1. Google Playで課金しiOS機器でプラン契約履歴を開く
     * 2. App Storeで課金しAndroidでプラン契約履歴を開く
     */
    const appAdditionalExternalLinks = computed(() => {
      if (DeviceInfo.isiOS() && paymentMethod === 'GooglePlay') {
        return [
          {
            text: I18n.tc('MypagePage.MypageContract.logPage.history.android'),
            url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.HISTORY.ANDROID,
          },
        ]
      }
      if (DeviceInfo.isAndroid() && paymentMethod === 'AppStore') {
        return [
          {
            id: 'iOS',
            text: I18n.tc('MypagePage.MypageContract.logPage.history.iOS'),
            url: Const.EXTERNAL_LINKS.IN_APP_PURCHASE.HISTORY.IOS,
          },
        ]
      }
      return []
    })

    return {
      planContractLogData,
      appMessageAndExternalLinks,
      appAdditionalMessage,
      appAdditionalExternalLinks,
    }
  },
})
</script>

<style lang="scss" scoped>
.mypage-contract-log-pane {
  display: flex;
  flex-flow: column nowrap;
  align-content: flex-start;
  justify-items: flex-start;
  gap: 24px;
  width: 100%;
  max-width: $responsiveMypageMaxWidth;
  height: calc(#{$mypageContentHeightHasSubHeader} - #{var(--safe-area-inset-bottom)});
  padding: 24px 16px;
  margin: 0 auto;
  text-align: left;
  overflow-y: auto;
  overflow-x: hidden;

  @media screen and (min-width: $responsiveTabletWidth) {
    max-width: calc(#{$responsiveTabletWidth} + 32px);
    padding-top: 66px;
    padding-bottom: 136px;
  }

  &__lead {
    font-weight: 700;
    font-size: min(#{convertPXtoVW(13)}, 13px);
    line-height: 1.54;
    letter-spacing: 0.04em;
    font-feature-settings: 'palt' on;
    color: $black;
  }

  &__message {
    margin-top: 6px;
    color: $gray75;
    font-size: 13px;
  }

  &__list {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    margin-top: 6px;
  }

  &__item {
    opacity: 0.8;

    &Link {
      color: $black80;
    }
  }
}
</style>
